import { z } from "zod";
import { safeString } from "./blocks.ts";
import { DOC_CODE_RE, newDocPrefixes } from "./doc-types.ts";
import { SchemaError } from "./errors.ts";

export type DocKind = "page" | "document" | "template";

export type DocMeta = {
  code?: string;
  subtitle?: string;
  docType?: string;
  audience?: "internal" | "client";
  client?: string;
  lang?: "en" | "nl";
  date?: string;
  version?: string;
  toc?: boolean;
  numbered?: boolean;
};

export type ResolvedMeta = Required<Pick<DocMeta, "audience" | "lang" | "numbered">> & DocMeta;

const DATE_RE = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/;

function isRealDate(value: string): boolean {
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const [, y, mo, d = "01"] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return (
    date.getUTCFullYear() === Number(y) && date.getUTCMonth() === Number(mo) - 1 && date.getUTCDate() === Number(d)
  );
}

export const DocMetaSchema: z.ZodType<DocMeta> = z
  .strictObject({
    code: safeString()
      .regex(DOC_CODE_RE, `code must be PREFIX-Words with prefix ${newDocPrefixes()}, e.g. RB-Tenant-Onboarding`)
      .optional(),
    subtitle: safeString().min(1).optional(),
    docType: safeString().min(1).optional(),
    audience: z.enum(["internal", "client"]).optional(),
    client: safeString().min(1).optional(),
    lang: z.enum(["en", "nl"]).optional(),
    date: safeString().refine(isRealDate, "date must be YYYY-MM or YYYY-MM-DD").optional(),
    version: safeString().optional(),
    toc: z.boolean().optional(),
    numbered: z.boolean().optional(),
  })
  .superRefine((m, ctx) => {
    if (m.audience === "client" && !m.client) {
      ctx.addIssue({ code: "custom", path: ["client"], message: 'client is required when audience is "client"' });
    }
  });

/** Apply defaults (audience, lang, numbered, date) and enforce the fields a "document" needs. Throws SchemaError. */
export function resolveMeta(kind: DocKind, meta: DocMeta, now: Date): ResolvedMeta {
  // C4: the code pattern is validated only for documents and templates; pages keep any code as-is.
  const parsed = DocMetaSchema.safeParse(kind === "page" ? { ...meta, code: undefined } : meta);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new SchemaError(issue?.message ?? "invalid meta", ["meta", ...(issue?.path ?? [])].join("."));
  }
  const m = kind === "page" && meta.code !== undefined ? { ...parsed.data, code: meta.code } : parsed.data;
  if (kind === "document") {
    for (const key of ["code", "subtitle", "docType"] as const) {
      if (!m[key]) throw new SchemaError(`${key} is required for a document`, `meta.${key}`);
    }
  }
  const audience = m.audience ?? "internal";
  return {
    ...m,
    audience,
    lang: m.lang ?? (audience === "client" ? "nl" : "en"),
    numbered: m.numbered ?? true,
    date: m.date ?? now.toISOString().slice(0, 7),
  };
}
